import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { LoyaltyProvider } from "@/contexts/LoyaltyContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { CheckoutProvider } from "@/contexts/CheckoutContext";
import { AuthProvider } from "@/contexts/AuthContext";
import CompareDrawer from "@/components/CompareDrawer";
import CompareBar from "@/components/CompareBar";
import ScrollToTop from "@/components/ScrollToTop";
import WishlistDrawer from "@/components/WishlistDrawer";
import { useCartSync } from "@/hooks/useCartSync";

// Eager load the index page for best LCP
import Index from "./pages/Index";

// Lazy load all other pages for code splitting
const ShopifyProductDetail = lazy(() => import("./pages/ShopifyProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
// Checkout is handled by Shopify - these imports removed
const GetFeatured = lazy(() => import("./pages/GetFeatured"));
const Apparel = lazy(() => import("./pages/Apparel"));
const Hats = lazy(() => import("./pages/Hats"));
const Sweatshirts = lazy(() => import("./pages/Sweatshirts"));
const TShirts = lazy(() => import("./pages/TShirts"));
const Accessories = lazy(() => import("./pages/Accessories"));
const ComfortGrips = lazy(() => import("./pages/ComfortGrips"));
const DryFeelGrips = lazy(() => import("./pages/DryFeelGrips"));
const GradientGrips = lazy(() => import("./pages/GradientGrips"));
const XtremeTackyGrips = lazy(() => import("./pages/XtremeTackyGrips"));
const ShopGrips = lazy(() => import("./pages/ShopGrips"));
const GripEnhancers = lazy(() => import("./pages/GripEnhancers"));
const GripProLanding = lazy(() => import("./pages/GripProLanding"));
const GripXTechnology = lazy(() => import("./pages/GripXTechnology"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Ambassador = lazy(() => import("./pages/Ambassador"));
const Wholesale = lazy(() => import("./pages/Wholesale"));
const WholesaleDashboard = lazy(() => import("./pages/wholesale/Dashboard"));
const GolfTowels = lazy(() => import("./pages/GolfTowels"));
const LeeWhitwellCollab = lazy(() => import("./pages/LeeWhitwellCollab"));
const Careers = lazy(() => import("./pages/Careers"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const PickleballGripGuide = lazy(() => import("./pages/PickleballGripGuide"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

// Auth pages
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));

// Affiliate pages
const AffiliateDashboard = lazy(() => import("./pages/affiliate/Dashboard"));
const AffiliateReferrals = lazy(() => import("./pages/affiliate/Referrals"));
const AffiliateDiscountCodes = lazy(() => import("./pages/affiliate/DiscountCodes"));
const AffiliatePayouts = lazy(() => import("./pages/affiliate/Payouts"));
const AffiliateAssets = lazy(() => import("./pages/affiliate/Assets"));
const AffiliateLeaderboard = lazy(() => import("./pages/affiliate/Leaderboard"));
const AffiliateCodeUsage = lazy(() => import("./pages/affiliate/CodeUsage"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AffiliateAdmin = lazy(() => import("./pages/admin/AffiliateAdmin"));
const PayoutAdmin = lazy(() => import("./pages/admin/PayoutAdmin"));
const TeamManagement = lazy(() => import("./pages/admin/TeamManagement"));
const SocialMediaAdmin = lazy(() => import("./pages/admin/SocialMediaAdmin"));
const InventoryAdmin = lazy(() => import("./pages/admin/Inventory"));

const WholesaleAdmin = lazy(() => import("./pages/admin/WholesaleAdmin"));
const WholesaleOrderDetail = lazy(() => import("./pages/admin/WholesaleOrderDetail"));
const LoyaltyAdmin = lazy(() => import("./pages/admin/LoyaltyAdmin"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const MarketingContacts = lazy(() => import("./pages/admin/MarketingContacts"));
const Announcements = lazy(() => import("./pages/admin/Announcements"));
const SubmitReview = lazy(() => import("./pages/SubmitReview"));
const CustomerDashboard = lazy(() => import("./pages/customer/Dashboard"));

const queryClient = new QueryClient();

// Loading fallback component for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

// Cart sync wrapper component
const CartSyncWrapper = ({ children }: { children: React.ReactNode }) => {
  useCartSync();
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <LoyaltyProvider>
              <CompareProvider>
                <CheckoutProvider>
                  <CartSyncWrapper>
                    <Toaster />
                    <Sonner />
                  <BrowserRouter>
                    <ScrollToTop />
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/product/:handle" element={<ShopifyProductDetail />} />
                        <Route path="/cart" element={<Cart />} />
                        {/* Redirect internal checkout routes to cart - checkout is handled by Shopify */}
                        <Route path="/checkout/shipping" element={<Navigate to="/cart" replace />} />
                        <Route path="/checkout/payment" element={<Navigate to="/cart" replace />} />
                        <Route path="/checkout/confirmation" element={<Navigate to="/cart" replace />} />
                        <Route path="/checkout" element={<Navigate to="/cart" replace />} />
                        <Route path="/get-featured" element={<GetFeatured />} />
                        <Route path="/apparel" element={<Apparel />} />
                        <Route path="/apparel/hats" element={<Hats />} />
                        <Route path="/apparel/sweatshirts" element={<Sweatshirts />} />
                        <Route path="/apparel/tshirts" element={<TShirts />} />
                        <Route path="/accessories" element={<Accessories />} />
                        <Route path="/grips" element={<ShopGrips />} />
                        <Route path="/grips/comfort" element={<ComfortGrips />} />
                        <Route path="/grips/dry-feel" element={<DryFeelGrips />} />
                        <Route path="/grips/gradient" element={<GradientGrips />} />
                        <Route path="/grips/xtreme-tacky" element={<XtremeTackyGrips />} />
                        <Route path="/grip-enhancers" element={<GripEnhancers />} />
                        {/* Legacy redirects */}
                        <Route path="/shop-grips" element={<Navigate to="/grips" replace />} />
                        <Route path="/comfort-grips" element={<Navigate to="/grips/comfort" replace />} />
                        <Route path="/dry-feel-grips" element={<Navigate to="/grips/dry-feel" replace />} />
                        <Route path="/gradient-grips" element={<Navigate to="/grips/gradient" replace />} />
                        <Route path="/xtreme-tacky-grips" element={<Navigate to="/grips/xtreme-tacky" replace />} />
                        <Route path="/grip-pro" element={<GripProLanding />} />
                        <Route path="/best-pickleball-grip-spray" element={<GripProLanding />} />
                        <Route path="/gripx-technology" element={<GripXTechnology />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/faq" element={<FAQ />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                        <Route path="/ambassador" element={<Ambassador />} />
                        <Route path="/wholesale" element={<Wholesale />} />
                        <Route path="/wholesale/dashboard" element={<WholesaleDashboard />} />
                        <Route path="/golf-towels" element={<GolfTowels />} />
                        <Route path="/collaborations/lee-whitwell" element={<LeeWhitwellCollab />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/overgrip-vs-replacement-grip-guide" element={<Navigate to="/blog/pickleball-overgrip-vs-replacement" replace />} />
                        <Route path="/blog/:slug" element={<BlogArticle />} />
                        <Route path="/pickleball-grip-guide" element={<PickleballGripGuide />} />
                        {/* Auth Routes */}
                        <Route path="/auth/login" element={<Login />} />
                        <Route path="/auth/register" element={<Register />} />
                        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                        <Route path="/auth/reset-password" element={<ResetPassword />} />
                        {/* Affiliate Routes */}
                        <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
                        <Route path="/affiliate/referrals" element={<AffiliateReferrals />} />
                        <Route path="/affiliate/discount-codes" element={<AffiliateDiscountCodes />} />
                        <Route path="/affiliate/payouts" element={<AffiliatePayouts />} />
                        <Route path="/affiliate/assets" element={<AffiliateAssets />} />
                        <Route path="/affiliate/leaderboard" element={<AffiliateLeaderboard />} />
                        <Route path="/affiliate/code-usage" element={<AffiliateCodeUsage />} />
                        {/* Admin Routes */}
                        <Route path="/admin" element={<AdminDashboard />} />
                        <Route path="/admin/affiliates" element={<AffiliateAdmin />} />
                        <Route path="/admin/payouts" element={<PayoutAdmin />} />
                        <Route path="/admin/team" element={<TeamManagement />} />
                        <Route path="/admin/social-media" element={<SocialMediaAdmin />} />
                        <Route path="/admin/customers" element={<Navigate to="/admin/marketing" replace />} />
                        <Route path="/admin/wholesale" element={<WholesaleAdmin />} />
                        <Route path="/admin/wholesale/orders/:id" element={<WholesaleOrderDetail />} />
                        <Route path="/admin/submissions" element={<Submissions />} />
                        <Route path="/admin/loyalty" element={<LoyaltyAdmin />} />
                        <Route path="/admin/marketing" element={<MarketingContacts />} />
                        <Route path="/admin/announcements" element={<Announcements />} />
                        <Route path="/admin/inventory" element={<InventoryAdmin />} />
                        <Route path="/dashboard" element={<CustomerDashboard />} />
                        <Route path="/review" element={<SubmitReview />} />
                        <Route path="/unsubscribe" element={<Unsubscribe />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                    <CompareBar />
                    <CompareDrawer />
                    <WishlistDrawer />
                  </BrowserRouter>

                  </CartSyncWrapper>
                </CheckoutProvider>
              </CompareProvider>
            </LoyaltyProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
